import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import { forwardRef, useMemo } from "react";
import { Skeleton } from "~/components/ui/skeleton";
import { branding } from "~/config/branding";
import { COMMAND_BAR_MAX_HEIGHT } from "../constants";
import type { ListItem } from "../getIconInfo";
import { useTopLevelNavigationCommands } from "../hooks/useCommandFeatureFlags";
import type { FilteredProject } from "../hooks/useFilteredProjects";
import type { Command, RecentItem, SearchResult } from "../types";
import { CommandGroup } from "./CommandGroup";

interface CommandBarResultsProps {
  query: string;
  allItems: ListItem[];
  selectedIndex: number;
  onSelect: (item: ListItem, newTab?: boolean) => void;
  onMouseEnter: (index: number) => void;
  filteredNavigation: Command[];
  filteredActions: Command[];
  filteredSupport: Command[];
  filteredTheme: Command[];
  filteredPage: Command[];
  searchResults: SearchResult[];
  filteredProjects: FilteredProject[];
  searchInTracesItem: ListItem | null;
  searchInDocsItem: ListItem | null;
  idResult: SearchResult | null;
  recentItemsLimited: RecentItem[];
  easterEggItem: ListItem | null;
  askLangyItem: ListItem | null;
  isLoading: boolean;
  /**
   * Whether a line marks the boundary with the field above the list. True
   * where the field and the list share one card, false where the list is its
   * own panel and already carries an edge of its own.
   */
  showTopDivider: boolean;
  /** The listbox's id, which the search field points at. */
  listId: string;
}

interface GroupConfig {
  label: string;
  items: ListItem[];
}

/**
 * Results section component for the command bar.
 * Renders all command groups with proper indexing.
 */
export const CommandBarResults = forwardRef<
  HTMLDivElement,
  CommandBarResultsProps
>(function CommandBarResults(
  {
    query,
    allItems,
    selectedIndex,
    onSelect,
    onMouseEnter,
    filteredNavigation,
    filteredActions,
    filteredSupport,
    filteredTheme,
    filteredPage,
    searchResults,
    filteredProjects,
    searchInTracesItem,
    searchInDocsItem,
    idResult,
    recentItemsLimited,
    easterEggItem,
    askLangyItem,
    isLoading,
    showTopDivider,
    listId,
  },
  ref,
) {
  const topLevelNavigation = useTopLevelNavigationCommands();

  // Build group configurations for empty query state
  const emptyQueryGroups = useMemo<GroupConfig[]>(
    () => [
      {
        label: "Recent",
        items: recentItemsLimited.map((d) => ({
          type: "recent" as const,
          data: d,
        })),
      },
      {
        label: "Go to",
        items: topLevelNavigation.map((d) => ({
          type: "command" as const,
          data: d,
        })),
      },
    ],
    [recentItemsLimited, topLevelNavigation],
  );

  // Build group configurations for query state
  const queryGroups = useMemo<GroupConfig[]>(() => {
    const groups: GroupConfig[] = [];

    // Easter egg at the very top
    if (easterEggItem) {
      groups.push({
        label: "Easter egg",
        items: [easterEggItem],
      });
    }

    if (idResult) {
      groups.push({
        label: "Jump to ID",
        items: [{ type: "search" as const, data: idResult }],
      });
    }

    if (filteredNavigation.length > 0) {
      groups.push({
        label: "Go to",
        items: filteredNavigation.map((d) => ({
          type: "command" as const,
          data: d,
        })),
      });
    }

    if (filteredActions.length > 0) {
      groups.push({
        label: "Actions",
        items: filteredActions.map((d) => ({
          type: "command" as const,
          data: d,
        })),
      });
    }

    if (filteredSupport.length > 0) {
      groups.push({
        label: "Help and support",
        items: filteredSupport.map((d) => ({
          type: "command" as const,
          data: d,
        })),
      });
    }

    if (filteredTheme.length > 0) {
      groups.push({
        label: "Theme",
        items: filteredTheme.map((d) => ({
          type: "command" as const,
          data: d,
        })),
      });
    }

    if (filteredPage.length > 0) {
      groups.push({
        label: "Page actions",
        items: filteredPage.map((d) => ({
          type: "command" as const,
          data: d,
        })),
      });
    }

    if (searchResults.length > 0) {
      groups.push({
        label: "Search results",
        items: searchResults.map((d) => ({
          type: "search" as const,
          data: d,
        })),
      });
    }

    if (filteredProjects.length > 0) {
      groups.push({
        label: "Switch project",
        items: filteredProjects.map((d) => ({
          type: "project" as const,
          data: d,
        })),
      });
    }

    return groups;
  }, [
    easterEggItem,
    idResult,
    filteredNavigation,
    filteredActions,
    filteredSupport,
    filteredTheme,
    filteredPage,
    searchResults,
    filteredProjects,
  ]);

  /**
   * The two things we can always offer, for any string at all. They are not
   * matches, and must never outrank one — see the ordering note in
   * `useCommandBarItems`, which this has to mirror exactly or the running
   * keyboard index stops agreeing with what is on screen.
   */
  const fallbackGroups = useMemo<GroupConfig[]>(() => {
    const groups: GroupConfig[] = [];
    if (searchInTracesItem) {
      groups.push({ label: "Search traces", items: [searchInTracesItem] });
    }
    if (searchInDocsItem) {
      groups.push({ label: "Search docs", items: [searchInDocsItem] });
    }
    return groups;
  }, [searchInTracesItem, searchInDocsItem]);

  // Ask Langy leads on an empty bar; while typing it sits under the real
  // matches and above the fallbacks.
  const groups = useMemo<GroupConfig[]>(() => {
    const askGroup: GroupConfig | null = askLangyItem
      ? { label: `Ask ${branding.assistantName}`, items: [askLangyItem] }
      : null;
    if (query === "") {
      return askGroup ? [askGroup, ...emptyQueryGroups] : emptyQueryGroups;
    }
    return [...queryGroups, ...(askGroup ? [askGroup] : []), ...fallbackGroups];
  }, [query, emptyQueryGroups, queryGroups, askLangyItem, fallbackGroups]);

  // Render groups with running index calculation
  const renderGroups = () => {
    let currentIndex = 0;

    return groups
      .filter((group) => group.items.length > 0)
      .map((group) => {
        const startIndex = currentIndex;
        currentIndex += group.items.length;

        return (
          <CommandGroup
            key={group.label}
            label={group.label}
            items={group.items}
            startIndex={startIndex}
            selectedIndex={selectedIndex}
            onSelect={onSelect}
            onMouseEnter={onMouseEnter}
            listId={listId}
          />
        );
      });
  };

  return (
    <Box
      ref={ref}
      data-testid="command-bar-results"
      // The results scroll inside the palette while the search field keeps
      // the focus; a named region the keyboard can also reach, so a long
      // list can be scrolled without a mouse.
      role="region"
      aria-label="Command palette results"
      tabIndex={0}
      maxHeight={COMMAND_BAR_MAX_HEIGHT}
      overflowY="auto"
      paddingBottom="var(--space-2)"
      {...(showTopDivider
        ? { borderTopWidth: "1px", borderColor: "var(--color-border-2)" }
        : {})}
    >
      <VStack
        id={listId}
        role="listbox"
        aria-label="Results"
        align="stretch"
        gap={0}
      >
        {renderGroups()}
      </VStack>
      {/* A search still running: a row shaped like a result, shown only
          once the wait passes 150ms. */}
      {query !== "" && isLoading && (
        <HStack
          height="var(--row-h)"
          paddingX="var(--space-5)"
          gap="var(--space-3)"
        >
          <Skeleton width="var(--icon-sm)" height="var(--icon-sm)" />
          <Skeleton height="var(--space-3)" flex={1} />
        </HStack>
      )}
      {query !== "" && allItems.length === 0 && !isLoading && (
        <Text
          paddingX="var(--space-5)"
          paddingY="var(--space-6)"
          fontSize="var(--text-13)"
          color="var(--color-fg-2)"
          textAlign="center"
        >
          No results for &ldquo;{query}&rdquo;
        </Text>
      )}
    </Box>
  );
});
